import { widgetApiBaseUrl } from '@/components/CampaignWidgetManager/widgetPreviewTokens'
import { env } from '@/lib/env'
import { buildLandingAccountWidgetScriptAttributes } from '@/lib/widgetEmbedSnippet'
import type { LandingCampaign } from '@/types/landing'

const WIDGET_READY_EVENT = 'legalprime:widget-ready'
const OPEN_RETRY_MS = [120, 220, 400, 700] as const
const RELOCATE_RETRY_MS = [0, 50, 120, 220, 400, 700] as const

type LPWidgetInstance = {
  open?: () => void
  openPurchase?: () => void
  openRankedPurchase?: (quantity: number, expectedAmountCents: number) => boolean | void
  startCouponCheckout?: (coupon: LandingPendingCouponPrize) => void
}

export type LandingPendingCouponPrize = {
  code: string
  label?: string
  discount_percent?: number
  discount_amount?: number
  min_purchase?: number
}

type WidgetReadyDetail = {
  instanceId?: string
  campaignId?: string
}

type LoadedWidgetEntry = {
  mountEl: HTMLDivElement
  readyPromise: Promise<string>
  instanceId: string | null
  thankYouPaymentId: number | null
  profileCheckout: boolean
  profileDonorId: number | null
}

const loadedByCampaignId = new Map<number, LoadedWidgetEntry>()

function widgetScriptUrl(spaOrigin: string): string {
  const backendBase = widgetApiBaseUrl() || spaOrigin
  return `${backendBase.replace(/\/+$/, '')}/js/widget.js?v=${encodeURIComponent(env.widgetAssetVersion)}`
}

function waitForWidgetReady(campaignId: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      document.removeEventListener(WIDGET_READY_EVENT, onReady)
      reject(new Error('widget_ready_timeout'))
    }, 20000)

    const onReady = (event: Event): void => {
      const detail = (event as CustomEvent<WidgetReadyDetail>).detail
      if (!detail || String(detail.campaignId) !== String(campaignId)) {
        return
      }
      const instanceId = detail.instanceId?.trim()
      if (!instanceId) {
        return
      }
      window.clearTimeout(timeoutId)
      document.removeEventListener(WIDGET_READY_EVENT, onReady)
      resolve(instanceId)
    }

    document.addEventListener(WIDGET_READY_EVENT, onReady)
  })
}

export function getWidgetOverlayElement(instanceId: string): HTMLElement | null {
  return document.getElementById(`${instanceId}_overlay`)
}

export function relocateWidgetOverlayTo(
  instanceId: string,
  container: HTMLElement
): boolean {
  const overlay = getWidgetOverlayElement(instanceId)
  if (!overlay) {
    return false
  }
  if (overlay.parentElement === container) {
    return true
  }
  container.appendChild(overlay)
  return true
}

function removeWidgetOverlay(instanceId: string): void {
  getWidgetOverlayElement(instanceId)?.remove()
}

export function teardownLandingCampaignWidget(campaignId: number): void {
  const entry = loadedByCampaignId.get(campaignId)
  if (entry?.instanceId) {
    removeWidgetOverlay(entry.instanceId)
  }
  if (entry) {
    entry.mountEl.remove()
    loadedByCampaignId.delete(campaignId)
  }
}

export function unloadLandingCampaignWidget(campaignId: number): void {
  teardownLandingCampaignWidget(campaignId)
}

export type LoadLandingCampaignWidgetOptions = {
  profileCheckout?: boolean
  profileDonorId?: number
  thankYouPaymentId?: number
  thankYouLuckyNumbers?: string[]
  thankYouCheckoutToken?: string
}

export function loadLandingCampaignWidget(
  campaign: LandingCampaign,
  options?: LoadLandingCampaignWidgetOptions
): Promise<string> {
  const campaignId = campaign.id
  const requestedThankYouPaymentId =
    options?.thankYouPaymentId && options.thankYouPaymentId > 0
      ? options.thankYouPaymentId
      : null
  const requestedProfileCheckout = options?.profileCheckout === true
  const requestedProfileDonorId =
    requestedProfileCheckout && options?.profileDonorId && options.profileDonorId > 0
      ? options.profileDonorId
      : null
  const existing = loadedByCampaignId.get(campaignId)
  if (existing) {
    if (
      existing.thankYouPaymentId === requestedThankYouPaymentId &&
      existing.profileCheckout === requestedProfileCheckout &&
      existing.profileDonorId === requestedProfileDonorId
    ) {
      return existing.readyPromise
    }
    teardownLandingCampaignWidget(campaignId)
  }

  const spaOrigin = window.location.origin.replace(/\/+$/, '')
  const readyPromise = waitForWidgetReady(campaignId)

  const mountEl = document.createElement('div')
  mountEl.hidden = true
  mountEl.setAttribute('data-landing-widget-mount', String(campaignId))

  const scriptEl = document.createElement('script')
  scriptEl.async = false
  scriptEl.src = widgetScriptUrl(spaOrigin)

  const attributes = buildLandingAccountWidgetScriptAttributes(campaign, {
    publicAppUrl: window.location.origin,
  })
  if (options?.profileCheckout) {
    attributes['data-profile-checkout'] = '1'
    if (options.profileDonorId && options.profileDonorId > 0) {
      attributes['data-profile-donor-id'] = String(options.profileDonorId)
    }
  }
  if (options?.thankYouPaymentId && options.thankYouPaymentId > 0) {
    attributes['data-thank-you-payment-id'] = String(options.thankYouPaymentId)
    if (options.thankYouCheckoutToken) {
      attributes['data-thank-you-checkout-token'] = options.thankYouCheckoutToken
    }
    if (options.thankYouLuckyNumbers?.length) {
      attributes['data-thank-you-lucky-numbers'] = JSON.stringify(
        options.thankYouLuckyNumbers
      )
    }
  }
  scriptEl.setAttribute('data-lp-widget-bootstrap', '1')
  for (const [name, value] of Object.entries(attributes)) {
    scriptEl.setAttribute(name, value)
  }

  const entry: LoadedWidgetEntry = {
    mountEl,
    readyPromise: readyPromise,
    instanceId: null,
    thankYouPaymentId: requestedThankYouPaymentId,
    profileCheckout: requestedProfileCheckout,
    profileDonorId: requestedProfileDonorId,
  }

  const loadPromise = readyPromise
    .then((instanceId) => {
      if (loadedByCampaignId.get(campaignId) !== entry) {
        return instanceId
      }
      entry.instanceId = instanceId
      return instanceId
    })
    .catch((error) => {
      if (loadedByCampaignId.get(campaignId) === entry) {
        teardownLandingCampaignWidget(campaignId)
      }
      throw error
    })

  entry.readyPromise = loadPromise

  scriptEl.addEventListener('error', () => {
    if (loadedByCampaignId.get(campaignId) === entry) {
      teardownLandingCampaignWidget(campaignId)
    }
    loadPromise.catch(() => undefined)
  })

  mountEl.appendChild(scriptEl)
  document.body.appendChild(mountEl)
  loadedByCampaignId.set(campaignId, entry)

  return loadPromise
}

function tryOpen(instanceId: string, newPurchase: boolean): boolean {
  const widget = window.LPWidgets?.[instanceId]
  if (newPurchase && widget?.openPurchase) {
    widget.openPurchase()
    return true
  }
  if (!widget?.open) {
    return false
  }
  widget.open()
  return true
}

function tryClickInjectedButton(instanceId: string): boolean {
  const button = document.getElementById(`${instanceId}_btn`)
  if (!(button instanceof HTMLButtonElement)) {
    return false
  }
  button.click()
  return true
}

function tryStartCouponCheckout(
  instanceId: string,
  coupon: LandingPendingCouponPrize
): boolean {
  const widget = window.LPWidgets?.[instanceId]
  if (!widget?.startCouponCheckout) {
    return false
  }
  widget.startCouponCheckout(coupon)
  return true
}

export function openLandingCampaignWidget(instanceId: string, newPurchase = false): void {
  if (tryOpen(instanceId, newPurchase) || tryClickInjectedButton(instanceId)) {
    return
  }

  let step = 0
  const retry = (): void => {
    if (tryOpen(instanceId, newPurchase) || tryClickInjectedButton(instanceId)) {
      return
    }
    if (step >= OPEN_RETRY_MS.length) {
      return
    }
    const delay = OPEN_RETRY_MS[step]
    step += 1
    window.setTimeout(retry, delay)
  }

  retry()
}

export function openLandingCampaignWidgetWithCoupon(
  instanceId: string,
  coupon: LandingPendingCouponPrize
): Promise<void> {
  if (tryStartCouponCheckout(instanceId, coupon)) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    let step = 0

    const retry = (): void => {
      if (tryStartCouponCheckout(instanceId, coupon)) {
        resolve()
        return
      }
      if (step >= OPEN_RETRY_MS.length) {
        reject(new Error('widget_coupon_checkout_unavailable'))
        return
      }
      const delay = OPEN_RETRY_MS[step]
      step += 1
      window.setTimeout(retry, delay)
    }

    retry()
  })
}

export async function loadAndOpenLandingCampaignWidgetWithCoupon(
  campaign: LandingCampaign,
  coupon: LandingPendingCouponPrize,
  options?: LoadLandingCampaignWidgetOptions
): Promise<string> {
  const instanceId = await loadLandingCampaignWidget(campaign, options)
  await openLandingCampaignWidgetWithCoupon(instanceId, coupon)
  return instanceId
}

export function openLandingCampaignWidgetForRanking(
  instanceId: string,
  quantity: number,
  expectedAmountCents: number
): Promise<void> {
  if (
    !Number.isSafeInteger(quantity) ||
    quantity < 1 ||
    !Number.isSafeInteger(expectedAmountCents) ||
    expectedAmountCents < 1
  )
    return Promise.reject(new Error('widget_ranking_invalid_amount'))

  return new Promise((resolve, reject) => {
    let step = 0
    const start = (): void => {
      const widget = window.LPWidgets?.[instanceId]
      if (widget?.openRankedPurchase) {
        if (widget.openRankedPurchase(quantity, expectedAmountCents) === false) {
          reject(new Error('widget_ranking_payment_pending'))
          return
        }
        resolve()
        return
      }
      if (step >= OPEN_RETRY_MS.length) {
        reject(new Error('widget_ranking_checkout_unavailable'))
        return
      }
      window.setTimeout(start, OPEN_RETRY_MS[step++])
    }
    start()
  })
}

export async function loadAndOpenLandingCampaignWidgetForRanking(
  campaign: LandingCampaign,
  quantity: number,
  expectedAmountCents: number,
  options?: LoadLandingCampaignWidgetOptions
): Promise<void> {
  const instanceId = await loadLandingCampaignWidget(campaign, options)
  await openLandingCampaignWidgetForRanking(instanceId, quantity, expectedAmountCents)
}

export function activateInlineLandingCampaignWidget(
  instanceId: string,
  container: HTMLElement
): Promise<boolean> {
  openLandingCampaignWidget(instanceId)

  return new Promise((resolve) => {
    let step = 0

    const tryRelocate = (): void => {
      if (relocateWidgetOverlayTo(instanceId, container)) {
        resolve(true)
        return
      }
      if (step >= RELOCATE_RETRY_MS.length) {
        resolve(false)
        return
      }
      const delay = RELOCATE_RETRY_MS[step]
      step += 1
      window.setTimeout(tryRelocate, delay)
    }

    requestAnimationFrame(() => {
      tryRelocate()
    })
  })
}

declare global {
  interface Window {
    LPWidgets?: Record<string, LPWidgetInstance>
  }
}
